import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ConnectionCostNotice } from '../components/credits/ConnectionCostNotice';
import { useAuth } from '../contexts/AuthContext';
import {
  getMarketplaceConnectionDisplayName,
  getMarketplaceConnectionExternalId,
} from '../utils/marketplaceConnections';

interface UserInfo {
  id: number;
  nickname: string;
  email: string;
  site_id: string;
  permalink: string;
}

interface MercadoLivreConnection {
  id: string;
  userId: number;
  nickname: string | null;
  connectionName?: string | null;
  displayName?: string;
  externalId?: string;
  status: string;
  expiresAt: string;
  lastRefreshAt: string;
  createdAt: string;
}

export function MercadoLivrePage() {
  const { user } = useAuth();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [connectionName, setConnectionName] = useState('');
  const [connections, setConnections] = useState<MercadoLivreConnection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [renamingConnectionId, setRenamingConnectionId] = useState<string | null>(null);

  useEffect(() => {
    loadConnections();

    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');

    if (success === 'true') {
      toast.success('Conexao com Mercado Livre estabelecida com sucesso.');
      loadConnections();
      window.history.replaceState({}, document.title, '/mercado-livre');
    } else if (error) {
      toast.error(`Erro na autenticacao: ${decodeURIComponent(error)}`);
      window.history.replaceState({}, document.title, '/mercado-livre');
    }
  }, []);

  const selectedConnection = useMemo(
    () => connections.find((connection) => connection.id === selectedConnectionId) || null,
    [connections, selectedConnectionId]
  );

  const loadConnections = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoadingConnections(false);
        return;
      }

      const response = await fetch('/api/mercado-livre/connections', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Falha ao carregar conexoes');
      }

      const data = await response.json();
      const nextConnections = data.success ? (data.data || []) : [];
      setConnections(nextConnections);

      if (nextConnections.length === 0) {
        setSelectedConnectionId('');
      } else if (!nextConnections.some((connection: MercadoLivreConnection) => connection.id === selectedConnectionId)) {
        setSelectedConnectionId(nextConnections[0].id);
      }
    } catch (error) {
      console.error('Failed to load connections:', error);
      toast.error('Nao foi possivel carregar as conexoes do Mercado Livre');
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleCredentialsSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast.error('Nao autenticado');
        return;
      }

      const response = await fetch('/api/mercado-livre/auth/start', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          connectionName,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Falha ao iniciar autenticacao');
      }

      window.location.href = data.authUrl;
    } catch (error) {
      console.error('Failed to start auth:', error);
      toast.error(error instanceof Error ? error.message : 'Falha ao iniciar autenticacao');
    }
  };

  const handleTestApi = async (connectionIdOverride?: string) => {
    const connectionId = connectionIdOverride || selectedConnectionId;
    if (!connectionId) {
      toast.error('Selecione uma conexao');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast.error('Nao autenticado');
        return;
      }

      const response = await fetch('/api/mercado-livre/test-api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          connectionId,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Falha ao testar conexao');
      }

      setUserInfo(data.data);
      setSelectedConnectionId(connectionId);
      toast.success('Conexao testada com sucesso');
    } catch (error) {
      console.error('Failed to test API:', error);
      toast.error(error instanceof Error ? error.message : 'Falha ao testar conexao');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConnection = async (connectionId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conexao?')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast.error('Nao autenticado');
        return;
      }

      const response = await fetch(`/api/mercado-livre/connections/${connectionId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Falha ao excluir conexao');
      }

      toast.success('Conexao excluida com sucesso');
      setUserInfo(null);
      await loadConnections();
    } catch (error) {
      console.error('Failed to delete connection:', error);
      toast.error(error instanceof Error ? error.message : 'Falha ao excluir conexao');
    }
  };

  const handleRenameConnection = async (connection: MercadoLivreConnection) => {
    const nextName = window.prompt(
      'Digite o nome da conexao',
      connection.connectionName || getMarketplaceConnectionDisplayName('mercadolivre', connection)
    );

    if (nextName === null) return;

    try {
      setRenamingConnectionId(connection.id);
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast.error('Nao autenticado');
        return;
      }

      const response = await fetch(`/api/mercado-livre/connections/${connection.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          connectionName: nextName,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Falha ao renomear conexao');
      }

      toast.success('Nome da conexao atualizado');
      await loadConnections();
    } catch (error) {
      console.error('Failed to rename connection:', error);
      toast.error(error instanceof Error ? error.message : 'Falha ao renomear conexao');
    } finally {
      setRenamingConnectionId(null);
    }
  };

  const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString('pt-BR');
  const copyText = async (value: string, label: string) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copiado`);
  };

  const downloadExtension = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error('Nao autenticado');
      return;
    }
    const response = await fetch('/api/shopee/browser-extension/download', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      toast.error('Falha ao baixar extensao');
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'integraia-marketplaces-extension.zip';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();

    if (diff <= 0) return 'Expirada';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }

    return `${hours}h ${minutes}m`;
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      revoked: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status as keyof typeof colors] || colors.expired}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">Integracao com Mercado Livre</h1>
        <div className="bg-white rounded-lg border border-yellow-100 shadow-sm p-6 mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Extensao para metricas Mercado Livre</h2>
              <p className="text-sm text-gray-500 mt-1">
                Para sincronizar metricas, instale a extensao, entre no Mercado Livre com a conta conectada e abra a pagina de metricas dos anuncios. A extensao captura a sessao dessa pagina.
              </p>
            </div>
            <button
              onClick={downloadExtension}
              className="bg-slate-900 hover:bg-slate-800 text-white font-medium py-2 px-5 rounded-lg transition-colors"
            >
              Baixar extensao
            </button>
          </div>

          <div className="mt-5 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
            Antes de sincronizar metricas do Mercado Livre, mantenha uma aba do <span className="font-semibold">mercadolivre.com.br</span> aberta na pagina de metricas/anuncios da conta. Se trocar de conta, atualize o Connection ID na extensao.
          </div>

          <div className="mt-5 rounded-lg border border-gray-200 p-4">
            <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Tenant ID</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-gray-50 px-2 py-1 text-xs text-gray-700">{user?.tenantId || 'Tenant nao identificado'}</code>
              <button onClick={() => copyText(user?.tenantId || '', 'Tenant ID')} className="text-sm text-blue-600 hover:text-blue-800">Copiar Tenant ID</button>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Instale em <span className="font-medium">chrome://extensions</span>, ative o modo desenvolvedor, carregue a pasta descompactada, cole o Tenant ID acima e copie o Connection ID no card da conta que vai usar nas metricas.
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Conexoes Mercado Livre</h2>
              <p className="text-sm text-gray-500 mt-1">Cada conta conectada pode receber um nome proprio.</p>
            </div>
            <button
              onClick={() => setShowCredentialsModal(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-6 rounded-lg transition-colors"
            >
              Adicionar conexao
            </button>
          </div>

          {loadingConnections ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nenhuma conexao encontrada.</p>
              <p className="text-sm mt-2">Clique em "Adicionar conexao" para conectar a primeira conta.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {connections.map((connection) => {
                const displayName = getMarketplaceConnectionDisplayName('mercadolivre', connection);
                const externalId = getMarketplaceConnectionExternalId('mercadolivre', connection);
                return (
                  <div key={connection.id} className="rounded-lg border border-gray-200 bg-white p-4 transition hover:border-yellow-200">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="truncate text-base font-semibold text-gray-900">{displayName}</h3>
                          {getStatusBadge(connection.status)}
                        </div>
                        <p className="mt-1 text-sm text-gray-600">{connection.nickname || `Usuario ${connection.userId}`}</p>
                        <p className="mt-1 text-xs text-gray-500">User ID: {externalId}</p>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="rounded bg-gray-50 p-3">
                        <div className="text-xs uppercase text-gray-500">Connection ID</div>
                        <div className="mt-1 truncate font-mono text-xs text-gray-700">{connection.id}</div>
                      </div>
                      <div className="rounded bg-gray-50 p-3">
                        <div className="text-xs uppercase text-gray-500">Expira em</div>
                        <div className="mt-1 text-gray-700">{connection.status === 'expired' ? 'Expirada' : getTimeRemaining(connection.expiresAt)}</div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-gray-500">Ultima atualizacao: {formatDateTime(connection.lastRefreshAt)}</div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button onClick={() => copyText(connection.id, 'Connection ID')} className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Copiar Connection ID</button>
                      <button onClick={() => handleTestApi(connection.id)} className="rounded-md border border-blue-200 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50">Testar</button>
                      <button onClick={() => handleRenameConnection(connection)} disabled={renamingConnectionId === connection.id} className="rounded-md border border-amber-200 px-3 py-1.5 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-60">{renamingConnectionId === connection.id ? 'Salvando...' : 'Renomear'}</button>
                      <button onClick={() => handleDeleteConnection(connection.id)} className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50">Excluir</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Testar API</h2>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Conta conectada</label>
            <select
              value={selectedConnectionId}
              onChange={(event) => setSelectedConnectionId(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="">Selecione uma conexao</option>
              {connections.map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {getMarketplaceConnectionDisplayName('mercadolivre', connection)} - User ID {getMarketplaceConnectionExternalId('mercadolivre', connection)}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => handleTestApi()}
            disabled={loading || !selectedConnection}
            className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            {loading ? 'Testando...' : 'Testar conexao API'}
          </button>

          {userInfo && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-700 mb-2">Informacoes da conta</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">ID:</span>
                  <p className="font-medium">{userInfo.id}</p>
                </div>
                <div>
                  <span className="text-gray-500">Nickname:</span>
                  <p className="font-medium">{userInfo.nickname}</p>
                </div>
                <div>
                  <span className="text-gray-500">Email:</span>
                  <p className="font-medium">{userInfo.email}</p>
                </div>
                <div>
                  <span className="text-gray-500">Site:</span>
                  <p className="font-medium">{userInfo.site_id}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {showCredentialsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90dvh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Adicionar conexao Mercado Livre</h2>
              <p className="text-sm text-gray-600 mb-4">A autenticacao sera feita pelo Hub Marketplace. Informe apenas um nome para identificar esta conta.</p>
              <form onSubmit={handleCredentialsSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome da conexao</label>
                  <input
                    type="text"
                    value={connectionName}
                    onChange={(event) => setConnectionName(event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: ML Loja Principal"
                  />
                </div>
                <div className="mb-4"><ConnectionCostNotice featureKey="conn_marketplace" /></div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCredentialsModal(false);
                      setConnectionName('');
                    }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    Conectar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}






